/**
 * Add/Edit Membership Plan dialog (v2 — DNKADM-0056 U1).
 *
 * A MUI `Dialog` over the core `v2_membership_plan` fields (handoff §5.9): **name**
 * (the tier name — required, unique on `(tenant, name)`), **description** (opt),
 * **available at** (the {@link LocationAvailabilityField} — the owned locations the plan
 * is sold at, empty ⇒ ALL locations), **term** (days), **price** (a plain decimal in the
 * tenant currency), **who can buy** (the Members and staff / Staff only toggle —
 * `staffOnly`, DNKADM-0381 / DNKAPI-0535; default members and staff), and **credit
 * refills** (the per-plan additive/replace toggle — `creditsAdditive`, DNKADM-0139 /
 * DNKAPI-0259; default replace).
 *
 * **A plan IS the tier** — there is no separate tier string, and (DNKAPI-0256) no PSP
 * provider/product-id fields: dink-api resolves the payment plan by the plan's own id.
 * The per-plan credit deposits are edited in this dialog (below the fields — see the
 * editor note below).
 *
 * The per-plan **credit grants** use the plan-only {@link PlanCreditGrantsEditor}
 * (DNKADM-0108 — recurring vs. first-purchase add-on, two sections; NOT the shared
 * `PlanCreditsEditor`, which Packages keep). The grant editor is:
 *  - **edit mode** — LIVE against the existing plan id (`listPlanCredits` /
 *    `setPlanCredit` / `deletePlanCredit`): each row Add/Remove hits the network
 *    immediately, independent of this dialog's field-Save.
 *  - **create mode** — STAGED locally (no id yet): grants (the full write shape — amount
 *    + recurrence + the add-on knobs) are collected in a ref, then on Save the plan is
 *    **created FIRST** (`POST /membership-plans` → its `{id}`) and the staged grants are
 *    attached AFTER via `PUT …/credits/{bucketId}` (you can't grant to a plan that
 *    doesn't exist yet — order matters).
 *
 * In **edit mode on a Draft plan** (DNKAPI-0261) the dialog shows a "Draft — not yet
 * live" info alert (noting, for a priced plan, that publishing also makes it billable)
 * and a **Publish** action that takes the plan live (`publishMembershipPlan`) then
 * `onSaved()`s. Active/Inactive plans show neither.
 *
 * In **edit mode** the dialog also hosts the plan's optional **access-hours cap**
 * ({@link PlanAccessHoursSection}, DNKADM-0315) — live against the plan id like the
 * grant editor; create mode shows a note that it becomes available once the plan exists.
 *
 * In **edit mode on a published plan with members on it** (DNKADM-0407 / DNKAPI-0562), the
 * dialog opens with a line saying how many members are on the plan and how many of them are
 * billed through Dink, read from the list row's `impact`. Changing the price (when any member
 * is billed through Dink) or the name then says, inside that field, what the change does to
 * those members. These are statements, never a gate: an absent `impact` shows none of them.
 * Credits save as soon as they change, so their notice and their one confirm (removing a
 * recurring grant cuts current members' next renewal) live in the grant editor, fed the same
 * member count — see {@link PlanCreditGrantsEditor}.
 *
 * Money is a plain decimal (NOT cents) — `price` is `DECIMAL(10,2)` in the tenant
 * currency (0049). A validation `400` / `409` (duplicate name) renders INLINE; anything
 * else raises an error toast (`classifyFormError`). Mirrors `PackageDialog` (0102).
 */
import { useCallback, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  createMembershipPlan,
  publishMembershipPlan,
  updateMembershipPlan,
  type MembershipPlan,
  type MembershipPlanImpact,
  type MembershipPlanInput,
} from '@/api/membershipPlans';
import {
  deletePlanCredit,
  listPlanCredits,
  setPlanCredit,
  type PlanCredit,
  type PlanCreditWrite,
} from '@/api/membershipTierCredits';
import type { ShowToast } from '@/components/toastContext';
import { formatMoney } from '@/utils/v2Format';
import { PlanCreditGrantsEditor } from './PlanCreditGrantsEditor';
import { PlanAccessHoursSection } from './PlanAccessHoursSection';
import { LocationAvailabilityField } from './LocationAvailabilityField';
import { classifyFormError } from '@/api/screenError';

/** A failed save: a rejected field inline, anything else a toast (DNKADM-0440). */
function classifyPlanSaveError(err: unknown) {
  return classifyFormError(err, 'That plan isn’t valid — check the name, provider, term, and price.', {
    toastFallback: 'The plan couldn’t be saved. Please try again.',
  });
}

export interface PlanDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The plan being edited, or `null` to add a new one. */
  plan: MembershipPlan | null;
  /** The tenant currency code (for the price adornment), or null while it loads. */
  currency: string | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  termDays?: string;
  price?: string;
}

/** "1 is" / "4 are" — the verb agrees with the count. */
function countIs(n: number): string {
  return `${n} ${n === 1 ? 'is' : 'are'}`;
}

/**
 * The impact line (DNKADM-0407 U1), e.g. "6 members are on this plan." + "4 are billed through
 * Dink, 2 are comps or staff-added."
 */
function impactLine(impact: MembershipPlanImpact): { lead: string; detail: string } {
  const n = impact.activeMemberships;
  const others =
    impact.notBilledByDink === 1
      ? '1 is a comp or staff-added'
      : `${countIs(impact.notBilledByDink)} comps or staff-added`;
  return {
    lead: `${n} ${n === 1 ? 'member is' : 'members are'} on this plan.`,
    detail: `${countIs(impact.billedByDink)} billed through Dink, ${others}.`,
  };
}

export function PlanDialog(props: PlanDialogProps) {
  // Remount the inner form per open / per edited plan so its inputs reseed cleanly.
  if (!props.open) return null;
  return <PlanDialogForm key={props.plan?.id ?? 'new'} {...props} />;
}

function PlanDialogForm({
  plan,
  currency,
  onClose,
  onSaved,
  showToast,
}: Omit<PlanDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = plan !== null;

  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [locationIds, setLocationIds] = useState<string[]>(plan?.locationIds ?? []);
  const [termDays, setTermDays] = useState(plan ? String(plan.termDays) : '');
  const [price, setPrice] = useState(plan ? String(plan.price) : '');
  const [creditsAdditive, setCreditsAdditive] = useState(plan?.creditsAdditive ?? false);
  const [staffOnly, setStaffOnly] = useState(plan?.staffOnly ?? false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Create mode has no id yet — grants are staged here (full write shape, incl.
  // recurrence + the add-on knobs) and flushed after the POST.
  const stagedRef = useRef<Map<string, PlanCreditWrite>>(new Map());
  const loadStaged = useCallback(
    async (_id: string): Promise<PlanCredit[]> =>
      Array.from(stagedRef.current, ([creditBucketId, w]) => ({
        creditBucketId,
        amount: w.amount,
        recurrence: w.recurrence ?? 'recurring',
        reEligibilityLapseMonths: w.reEligibilityLapseMonths ?? 0,
        expiryDays: w.expiryDays ?? 0,
      })),
    [],
  );
  const setStaged = useCallback(async (_id: string, bucketId: string, input: PlanCreditWrite) => {
    stagedRef.current.set(bucketId, input);
  }, []);
  const deleteStaged = useCallback(async (_id: string, bucketId: string) => {
    stagedRef.current.delete(bucketId);
  }, []);

  function validate(): MembershipPlanInput | null {
    const errors: FieldErrors = {};

    const tName = name.trim();
    if (tName === '') errors.name = 'Enter a name.';

    const term = Number(termDays);
    if (termDays.trim() === '' || !Number.isFinite(term) || !Number.isInteger(term) || term <= 0) {
      errors.termDays = 'Enter a whole number of days, greater than 0.';
    }

    const priceValue = Number(price);
    if (price.trim() === '' || !Number.isFinite(priceValue) || priceValue < 0) {
      errors.price = 'Enter a price (0 or more).';
    }

    if (errors.name || errors.termDays || errors.price) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: MembershipPlanInput = {
      name: tName,
      termDays: term,
      price: priceValue,
      // Always sent — `[]` ⇒ available at all locations, else the selected set.
      locationIds,
      creditsAdditive,
      // Always sent — who can buy is set here at creation and on every edit (DNKADM-0381).
      staffOnly,
    };
    const d = description.trim();
    if (d !== '') input.description = d;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && plan) {
        // Fields via PUT; deposits are managed live by the editor (independent sub-resource).
        await updateMembershipPlan(plan.id, input);
      } else {
        // Create the plan FIRST, THEN attach the staged deposits against its id.
        const created = await createMembershipPlan(input);
        const newId = created?.id ?? null;
        if (newId !== null) {
          for (const [bucketId, write] of stagedRef.current) {
            await setPlanCredit(newId, bucketId, write);
          }
        }
      }
      // DNKADM-0320: confirm the save (after every write resolves; one toast per action).
      showToast(isEdit ? 'Plan saved' : 'Plan added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyPlanSaveError(err);
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  // Publish a Draft plan (edit mode only) — no fields, so no validation step; the
  // updated row is refetched by the caller. For a priced plan this also registers it
  // with pay-api (DNKAPI-0265 §5) — the confirmation copy in the Alert says so.
  async function handlePublish() {
    if (!plan) return;
    setServerError(null);
    setSaving(true);
    try {
      await publishMembershipPlan(plan.id);
      // DNKADM-0320: the publish path gets its own message, distinct from save.
      showToast('Plan published', 'success');
      onSaved();
    } catch (err) {
      const view = classifyPlanSaveError(err);
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const termId = `${ids}-term`;
  const priceId = `${ids}-price`;

  // DNKADM-0407: who is on the plan. A Draft has nobody on it; an absent `impact` is unknown —
  // both show nothing, and nothing here ever blocks Save.
  const impact = isEdit && plan && plan.status !== 'DRAFT' ? (plan.impact ?? null) : null;
  const hasMembers = impact !== null && impact.activeMemberships > 0;
  const summary = hasMembers ? impactLine(impact) : null;
  const nameChanged = isEdit && plan !== null && name.trim() !== plan.name;
  const priceChanged =
    isEdit && plan !== null && price.trim() !== String(plan.price) && Number(price) !== plan.price;
  const savedPrice = plan
    ? currency
      ? formatMoney(plan.price, currency)
      : plan.price.toFixed(2)
    : '';
  const nameNotice =
    hasMembers && nameChanged ? 'Current members will see the new name right away.' : null;
  const priceNotice =
    impact !== null && impact.billedByDink > 0 && priceChanged
      ? `Current members keep paying ${savedPrice}. Only new members pay the new price.`
      : null;

  // Edit ⇒ the editor is live against the plan id; add ⇒ it stages into the ref.
  const editorProps =
    isEdit && plan
      ? {
          planId: plan.id,
          loadCredits: listPlanCredits,
          setCredit: setPlanCredit,
          deleteCredit: deletePlanCredit,
        }
      : {
          planId: '',
          loadCredits: loadStaged,
          setCredit: setStaged,
          deleteCredit: deleteStaged,
        };

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="md" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit plan' : 'Add plan'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {isEdit && plan?.status === 'DRAFT' && (
            <Alert severity="info" role="status">
              Draft — not yet live.
              {plan.price > 0 ? ' Publishing this plan also makes it billable.' : ''}
            </Alert>
          )}
          {summary && (
            <Alert severity="info" role="status">
              <strong>{summary.lead}</strong> {summary.detail}
            </Alert>
          )}
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name ?? nameNotice ?? 'The membership tier this plan grants.'}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />

          {/* The owned locations this plan is available at (empty ⇒ all locations). */}
          <LocationAvailabilityField
            value={locationIds}
            onChange={setLocationIds}
            disabled={saving}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              id={termId}
              label="Term"
              value={termDays}
              onChange={(e) => setTermDays(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.termDays)}
              helperText={fieldErrors.termDays}
              sx={{ flex: 1 }}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
                htmlInput: { inputMode: 'numeric', 'aria-label': 'Term (days)' },
              }}
            />
            <TextField
              id={priceId}
              label="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              disabled={saving}
              size="small"
              error={Boolean(fieldErrors.price)}
              helperText={fieldErrors.price ?? priceNotice}
              sx={{ flex: 1 }}
              slotProps={{
                ...(currency
                  ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } }
                  : {}),
                htmlInput: { inputMode: 'decimal', 'aria-label': 'Price' },
              }}
            />
          </Stack>

          {/* Who can buy (DNKADM-0381 / DNKAPI-0535) — orthogonal to Active/Inactive: a
              staff-only plan stays sellable at the desk and off the member app. */}
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.75 }}>Who can buy</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={staffOnly ? 'staff' : 'members'}
              onChange={(_e, value: string | null) => {
                if (value !== null) setStaffOnly(value === 'staff');
              }}
              aria-label="Who can buy"
              disabled={saving}
            >
              <ToggleButton value="members" aria-label="Members and staff">
                Members and staff
              </ToggleButton>
              <ToggleButton value="staff" aria-label="Staff only">
                Staff only
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              {staffOnly
                ? 'Members won’t see this plan. The front desk can still sell it.'
                : 'Members can buy this plan themselves, and the front desk can sell it.'}
            </Typography>
          </Box>

          {/* Per-plan credit refill mode (DNKADM-0139 / DNKAPI-0259) — governs how this
              plan's recurring credit buckets refill at renewal. Default: Replace. */}
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.75 }}>Credit refills</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={creditsAdditive ? 'additive' : 'replace'}
              onChange={(_e, value: string | null) => {
                if (value !== null) setCreditsAdditive(value === 'additive');
              }}
              aria-label="Credit refills"
              disabled={saving}
            >
              <ToggleButton value="additive" aria-label="Additive">
                Additive
              </ToggleButton>
              <ToggleButton value="replace" aria-label="Replace">
                Replace
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              {creditsAdditive
                ? 'Each renewal adds a fresh lot; leftovers stack until they expire.'
                : 'Each renewal zeroes the remaining lots, then grants one fresh lot (no carryover).'}
            </Typography>
          </Box>

          <PlanCreditGrantsEditor
            {...editorProps}
            showToast={showToast}
            activeMemberships={impact?.activeMemberships ?? null}
          />

          {/* Per-plan access-hours cap (DNKADM-0315 / DNKAPI-0457) — LIVE against the
              plan id (its own GET/PUT/DELETE, independent of this dialog's field-Save),
              so it is edit-mode only: a plan must exist before it can carry a cap. */}
          {isEdit && plan ? (
            <PlanAccessHoursSection planId={plan.id} showToast={showToast} />
          ) : (
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.75 }}>Access hours</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Available once the plan is created — edit the plan to narrow when its members can
                book. Until then the plan gets full facility hours.
              </Typography>
            </Box>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        {isEdit && plan?.status === 'DRAFT' && (
          <Button
            onClick={() => void handlePublish()}
            disabled={saving}
            color="inherit"
            variant="outlined"
          >
            Publish
          </Button>
        )}
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save plan' : 'Add plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
